import {
  Component,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import type { User } from '@shared/models/user';
import { AuthService, UserModeService } from '@tvd/data-access/auth';
import { PwaService } from '@tvd/data-access/pwa';
import { DropdownModule } from '@tvd/design-system/dropdown';
import { Icon } from '@tvd/design-system/icon';
import { Toggle } from '@tvd/design-system/toggle';
import { ThemeToggle } from '@tvd/ui/theme-toggle';
import { createLanguageSwitcher } from '@tvd/utils/language-switch';
import { Avatar } from '../avatar';
import { LanguagePicker } from '../language-picker';
import { NamePipe } from '../name/name.pipe';

@Component({
  selector: 'tvd-nav-drawer-user',
  imports: [
    Toggle,
    Avatar,
    DropdownModule,
    LanguagePicker,
    NamePipe,
    RouterLink,
    TranslatePipe,
    Icon,
    ThemeToggle,
  ],
  template: `
    <div class="flex items-center gap-3 pt-2">
      @if (hasModes()) {
        <tvd-toggle
          [checked]="userMode() === 'customer'"
          (checkedChange)="toggleMode()"
          [iconOn]="'person'"
          [iconOff]="'storefront'"
        />
      }
      @if (user(); as u) {
        <div
          class="flex py-2 items-center gap-2 text-sm cursor-pointer w-full"
          [class.justify-center]="isCollapsed()"
          tvdDropdownAnchor
          #avatarAnchor="tvdDropdownAnchor"
        >
          <tvd-avatar [user]="u" size="sm" (click)="userMenu.toggle()" />
          @if (!isCollapsed()) {
            <span class="truncate">{{ u | name }}</span>
          }
        </div>
        <tvd-dropdown #userMenu [anchor]="avatarAnchor" class="!max-w-[250px]">
          <div tvdDropdownItem class="w-full px-3 py-2 rounded-t-md">
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <tvd-icon icon="settings" class="text-sm" />
                <span>{{ 'settings.theme' | translate }}</span>
              </div>
              <tvd-theme-toggle />
            </div>
          </div>
          <div class="w-full px-2 py-1">
            <tvd-language-picker
              class="w-full"
              [currentLang]="lang.currentLang()"
              (currentLangChange)="lang.selectLang($event)"
            />
          </div>
          @if (pwa.isInstallable()) {
            <button
              tvdDropdownItem
              class="w-full text-left px-3 py-2"
              (click)="installApp()"
            >
              <div class="flex items-center gap-2">
                <tvd-icon icon="open_in_new" class="text-sm" />
                <span>{{ 'pwa.installApp' | translate }}</span>
              </div>
            </button>
          }
          <button
            tvdDropdownItem
            class="w-full text-left px-3 py-2"
            [routerLink]="['/settings']"
          >
            <div class="flex items-center gap-2">
              <tvd-icon icon="person" class="text-sm" />
              <span>{{ 'settings.myAccount' | translate }}</span>
            </div>
          </button>
          <button
            tvdDropdownItem
            class="w-full text-left px-3 py-2"
            [routerLink]="['/support/chat']"
          >
            <div class="flex items-center gap-2">
              <tvd-icon icon="person" class="text-sm" />
              <span>{{ 'support.chat' | translate }}</span>
            </div>
          </button>
          <button
            tvdDropdownItem
            class="w-full text-left px-3 py-2 rounded-b-md"
            (click)="logout.emit()"
          >
            <div class="flex items-center gap-2 text-red-600">
              <tvd-icon icon="open_in_new" class="text-sm" />
              <span>{{ 'auth.logout' | translate }}</span>
            </div>
          </button>
        </tvd-dropdown>
      }
    </div>
  `,
})
export class NavDrawerUser {
  readonly isCollapsed = model(false);
  readonly user = input.required<User>();

  readonly logout = output();

  readonly #mode = inject(UserModeService);
  readonly #auth = inject(AuthService);
  readonly pwa = inject(PwaService);

  readonly userMode = this.#mode.userMode;
  readonly hasModes = this.#mode.hasModes;

  /**
   * The shared language switcher (slice 02) wired to the main-app
   * `AuthService`. Owns the locale math + persistence (Storage + user PATCH);
   * the `<tvd-language-picker>` stays presentational.
   */
  protected readonly lang = createLanguageSwitcher({
    userId: () => this.#auth.user.value()?.id,
    updateUser: (userId, patch) => this.#auth.updateUser(userId, patch),
  });

  toggleMode(): void {
    this.#mode.toggleMode();
  }

  installApp(): void {
    if (this.pwa.isIOS()) {
      this.pwa.showIOSInstructions();
    } else {
      this.pwa.install();
    }
  }
}
